import { Option } from "@jutro/types";
import dayjs from "dayjs";
import { z } from "zod";
import {
  mapFromSlotTypeToVisitType,
  roundTimeToNearestFiveMinutes,
} from "components/ScheduleVisitForm/tools";
import { ScheduleVisitTileData } from "components/ScheduleVisitForm/types";
import {
  DoctorGetVisitDataForEditFormQuery,
  DoctorMSPShiftVisitType,
} from "lib/graphql/megaSchema";

type SlotDuration = 5 | 10 | 12 | 15 | 20 | 30 | 45 | 60;

type DurationOption = Option<SlotDuration>;

type OptionConfig = {
  visibility: "all" | "force" | "hidden";
  label: string;
  onlyForChildren?: boolean;
};

const visitTypeLabelMap: Record<DoctorMSPShiftVisitType, OptionConfig> = {
  IN_PERSON: { visibility: "all", label: "Osobista" },
  TELEMEDICAL: { visibility: "all", label: "Telemedyczna" },
  DOCTOR_TELEMEDICAL_INFECTION: {
    visibility: "all",
    label: "Telemedyczna infekcyjna",
  },
  NURSE_IN_PERSON_INFECTION: {
    visibility: "all",
    label: "Infekcyjna pielęgniarska",
  },
  PROCEDURE: { visibility: "all", label: "Zabiegowa" },
  COLLECTION: { visibility: "all", label: "Pobraniowa" },
  RADIOLOGY_APPOINTMENT: { visibility: "all", label: "USG" },
  EXPRESS_APPOINTMENT: { visibility: "all", label: "Ekspresowa" },
  SICK_CHILDREN: {
    visibility: "all",
    label: "Dzieci chore",
    onlyForChildren: true,
  },
  MIDWIFE_IN_PERSON: { visibility: "all", label: "Edukacja przedporodowa" },
  HEALTHY_CHILDREN_CHECKUP: {
    visibility: "all",
    label: "Dzieci bilans",
    onlyForChildren: true,
  },
  HEALTHY_CHILDREN_CHECKUP_AND_VACCINE: {
    label: "Dzieci bilans + szczepienie",
    visibility: "all",
    onlyForChildren: true,
  },
  NURSE_HEALTHY_CHILDREN: {
    label: "Dzieci zdrowe pielęgniarka",
    visibility: "all",
    onlyForChildren: true,
  },
  HEALTHY_CHILDREN_PATRONAGE: {
    visibility: "all",
    label: "Dzieci patronaż",
    onlyForChildren: true,
  },
  HEALTHY_CHILDREN_VACCINE: {
    visibility: "all",
    label: "Dzieci szczepienia",
    onlyForChildren: true,
  },
  NURSE_IN_PERSON: { visibility: "all", label: "Osobista pielęgniarska" },
  DIETETICS: {
    visibility: "force",
    label: "Dietetyczna",
  },
  DOCTOR_HOUSE_CALL: { visibility: "force", label: "Domowa" },
  NURSE_HOUSE_CALL: { visibility: "force", label: "Domowa pielęgniarska" },
  SPECIALIST: { visibility: "force", label: "Specjalistyczna" },
  MIDWIFE_HOUSE_CALL: { visibility: "force", label: "Patronaż" },
  PRESCRIPTION_APPOINTMENT: { visibility: "hidden", label: "Receptowa" },
};

const visitTypes = Object.keys(visitTypeLabelMap) as DoctorMSPShiftVisitType[];

export const getVisitTypeOptions = (
  mode: ScheduleVisitTileData["mode"],
  isPatientChild: boolean,
) => {
  const visitTypeLabelMapVisibleEntries = Object.entries(
    visitTypeLabelMap,
  ).filter(([, { visibility, onlyForChildren }]) => {
    if (visibility === "hidden") {
      return false;
    }

    if (onlyForChildren && !isPatientChild) {
      return false;
    }

    return true;
  });

  if (mode.startsWith("force")) {
    return visitTypeLabelMapVisibleEntries.map(([value, { label }]) => ({
      label,
      value,
    })) as Option<DoctorMSPShiftVisitType>[];
  }

  return visitTypeLabelMapVisibleEntries
    .filter(([, { visibility }]) => visibility === "all")
    .map(([value, { label }]) => ({
      label,
      value,
    })) as Option<DoctorMSPShiftVisitType>[];
};

export const telemedicalVisitTypes: DoctorMSPShiftVisitType[] = [
  "TELEMEDICAL",
  "DOCTOR_TELEMEDICAL_INFECTION",
  "EXPRESS_APPOINTMENT",
];

export const durationOptions: DurationOption[] = [
  {
    label: "5 minut",
    value: 5,
  },
  {
    label: "10 minut",
    value: 10,
  },
  {
    label: "12 minut",
    value: 12,
  },
  {
    label: "15 minut",
    value: 15,
  },
  { label: "20 minut", value: 20 },
  {
    label: "30 minut",
    value: 30,
  },
  {
    label: "45 minut",
    value: 45,
  },
  {
    label: "60 minut",
    value: 60,
  },
];

export const scheduleFormSchema = z
  .object({
    mode: z.enum(["create", "edit", "view", "forceCreate", "forceEdit"]),
    visitType: z.enum(visitTypes as [DoctorMSPShiftVisitType]),
    organizationId: z.string(),
    voivodeshipId: z.string().nullable(),
    officeId: z.string().nullable(),
    staffId: z.string().nullable(),
    duration: z.number().nullable(),
    plannedTime: z.string().nullable(),
    plannedDate: z.string().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.mode.startsWith("force")) {
      const requiredFields: (keyof typeof data)[] = [
        "staffId",
        "duration",
        "plannedTime",
        "plannedDate",
      ];

      if (telemedicalVisitTypes.includes(data.visitType)) {
        requiredFields.push("voivodeshipId");
      }

      if (!telemedicalVisitTypes.includes(data.visitType)) {
        requiredFields.push("officeId");
      }

      requiredFields.forEach((field) => {
        if (!data[field]) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [field],
            message: "To pole jest wymagane",
          });
        }
      });
    }
  });

export type ScheduleFormSchema = z.infer<typeof scheduleFormSchema>;

export const defaultScheduleFormData: Omit<
  ScheduleFormSchema,
  "mode" | "organizationId"
> = {
  visitType: "IN_PERSON",
  voivodeshipId: null,
  officeId: null,
  staffId: null,
  duration: null,
  plannedTime: roundTimeToNearestFiveMinutes(dayjs()),
  plannedDate: dayjs().format("YYYY-MM-DD"),
};

export const prepareDefaultValue = (
  editedVisitData: DoctorGetVisitDataForEditFormQuery | undefined,
  mode: ScheduleVisitTileData["mode"],
  organizationId: string,
): ScheduleFormSchema => {
  if (!editedVisitData) {
    return { mode, organizationId, ...defaultScheduleFormData };
  }

  const resolvedOrganizationId =
    editedVisitData.doctorVisit.type === "VIRTUAL"
      ? organizationId
      : editedVisitData.doctorVisit.organizationId?.replace(/\d+$/, "");

  return {
    mode,
    ...defaultScheduleFormData,
    visitType: editedVisitData.doctorVisit.slotType
      ? mapFromSlotTypeToVisitType(editedVisitData.doctorVisit.slotType)
      : defaultScheduleFormData.visitType,
    staffId: editedVisitData.doctorVisit.doctor?.id ?? null,
    organizationId: resolvedOrganizationId!,
  };
};
